import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState(token ? 'verifying' : 'missing_token');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch('/api/auth/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setStatus(data.already_verified ? 'already_verified' : 'verified');
          return;
        }
        setStatus('invalid');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="max-w-sm mx-auto px-4 py-16 text-center">
      {status === 'verifying' && (
        <>
          <Loader2 className="text-slate-400 mx-auto mb-3 animate-spin" size={32} />
          <h1 className="text-xl font-bold mb-2">Verifying your email…</h1>
          <p className="text-sm text-slate-600">This will only take a moment.</p>
        </>
      )}

      {status === 'verified' && (
        <>
          <CheckCircle className="text-emerald-500 mx-auto mb-3" size={32} />
          <h1 className="text-xl font-bold mb-2">Email verified</h1>
          <p className="text-sm text-slate-600 mb-4">
            Your email is confirmed. You can browse and purchase courses.
          </p>
          <Link
            to="/dashboard"
            className="inline-block bg-brand text-white rounded px-5 py-2 text-sm font-medium"
          >
            Go to dashboard
          </Link>
        </>
      )}

      {status === 'already_verified' && (
        <>
          <CheckCircle className="text-emerald-500 mx-auto mb-3" size={32} />
          <h1 className="text-xl font-bold mb-2">Email already verified</h1>
          <p className="text-sm text-slate-600 mb-4">
            Your email has already been confirmed. You're all set!
          </p>
          <Link
            to="/library"
            className="inline-block bg-brand text-white rounded px-5 py-2 text-sm font-medium"
          >
            Browse courses
          </Link>
        </>
      )}

      {(status === 'invalid' || status === 'missing_token') && (
        <>
          <AlertCircle className="text-rose-500 mx-auto mb-3" size={32} />
          <h1 className="text-xl font-bold mb-2">
            {status === 'missing_token' ? 'No verification token' : 'Link expired or invalid'}
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            {status === 'missing_token'
              ? 'This page expects a verification token in the URL. Try clicking the link in your email again.'
              : "This verification link has expired or has already been used. Log in to your account — we'll show you how to request a new one if you still need to verify."}
          </p>
          <Link
            to="/login"
            className="inline-block text-sm text-brand underline"
          >
            Go to log in
          </Link>
        </>
      )}

      {status === 'error' && (
        <>
          <AlertCircle className="text-rose-500 mx-auto mb-3" size={32} />
          <h1 className="text-xl font-bold mb-2">Network error</h1>
          <p className="text-sm text-slate-600">Try refreshing the page.</p>
        </>
      )}
    </div>
  );
}
